import { getOperatingSystem } from "../../../../util/common";
import { isChromeOSWebBrowser } from "../../../../util/webPushNotifications";

export type IPushNotificationProvider = ReturnType<
  typeof PushNotificationProviderFactory
>;

export const PushNotificationProviderFactory = ({
  circadiaEngineProvider,
  APIProvider,
  AuthService,
  apiRoute,
}: {
  circadiaEngineProvider: ICircadiaEngineProvider;
  APIProvider: IAPIProvider;
  AuthService: IAuthService;
  apiRoute: string;
}) => {
  const { PUT, DELETE } = APIProvider;

  const getFCMToken = async () => {
    const token = await window.electron.getFCMToken();
    if (token.status === "success") {
      return token.token!;
    }
    return null;
  };

  const getToken = async () => {
    let notificationToken: string | null = null;

    if (window.electron) {
      notificationToken = await getFCMToken();
    } else if (circadiaEngineProvider.isWindowCircadia()) {
      notificationToken = await circadiaEngineProvider.getDeviceToken();
    } else {
      // Check if this is a ChromeOS device before attempting FCM
      const { webPushManager, isChromeOSWebBrowser } = await import(
        "../../../../util/webPushNotifications"
      );

      if (!isChromeOSWebBrowser()) {
        return null;
      }

      // Set Firebase config if available
      const firebaseConfig = {
        apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
        authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
        projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
        storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
        messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
        appId: import.meta.env.VITE_FIREBASE_APP_ID,
      };

      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;

      if (!firebaseConfig.projectId || !vapidKey) {
        console.warn(
          "⚠️ [ChromeOS] Firebase configuration not complete for web push notifications"
        );
        return null;
      }

      // Initialize Firebase with config
      webPushManager.setFirebaseConfig(firebaseConfig, vapidKey);

      // Initialize FCM if not already done
      const initialized = await webPushManager.initialize();
      if (!initialized) {
        return null;
      }

      // Send Firebase config to service worker
      if ("serviceWorker" in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: "FIREBASE_CONFIG",
          config: firebaseConfig,
        });
      }

      // Check current permission status (don't auto-request)
      const currentPermission = Notification.permission;

      let fcmToken: string | null = null;

      if (currentPermission === "granted") {
        // Permission already granted - get or create FCM token
        fcmToken = await webPushManager.getExistingSubscription();
        if (!fcmToken) {
          fcmToken = await webPushManager.subscribeToPushNotifications();
        }

        if (fcmToken) {
          // Setup foreground message handler
          webPushManager.setupForegroundMessageHandler(() => {});

          notificationToken = fcmToken;
        }
      } else {
        // Permission not granted - user needs to enable notifications manually

        return null; // Don't auto-request - let user enable manually
      }
    }

    return notificationToken;
  };

  const registerDeviceForPushNotifications = async () => {
    const notificationToken = await getToken();
    if (!notificationToken) {
      console.warn("❌ Error, Fetching Notification Token ");
      return { success: false };
    }

    const { getAuthenticationToken } = AuthService;
    // Determine the operating system and platform
    const osType = getOperatingSystem({ circadiaEngineProvider });
    const isWebBrowser =
      !window.electron &&
      !circadiaEngineProvider.isWindowCircadia() &&
      isChromeOSWebBrowser();
    const payloadToSend = {
      notification_token:
        osType === "android"
          ? String(notificationToken).trim()
          : String(notificationToken).trim().replace(/\0/g, ""),
      os_type: isWebBrowser ? (osType === "chromeos" ? "web" : "web") : osType,
      platform: isWebBrowser ? "web" : "native",
    };

    let deviceNotificationResponse = "";
    try {
      if (
        window.electron ||
        circadiaEngineProvider.isWindowCircadia() ||
        (!window.electron && !circadiaEngineProvider.isWindowCircadia())
      ) {
        deviceNotificationResponse = await PUT(apiRoute, payloadToSend, {
          Authorization: `Bearer ${await getAuthenticationToken()}`,
        });
      }
    } catch (e) {
      console.error("❌ Error, Push notification registration failed:", e);
    }

    return { success: true, data: deviceNotificationResponse };
  };

  const unregisterDeviceForPushNotifications = async () => {
    // Add timeout to prevent hanging during logout
    const getTokenWithTimeout = async (
      timeoutMs = 3000
    ): Promise<string | null> => {
      return Promise.race([
        getToken(),
        new Promise<null>((resolve) =>
          setTimeout(() => {
            console.warn(
              "⚠️ [Logout] Token fetch timeout - continuing logout anyway"
            );
            resolve(null);
          }, timeoutMs)
        ),
      ]);
    };

    let notificationToken: string | null = null;
    try {
      notificationToken = await getTokenWithTimeout(3000);
    } catch (error) {
      console.warn("⚠️ [Logout] Error fetching token during logout:", error);
    }

    if (!notificationToken) {
      console.warn(
        "⚠️ [Logout] No notification token - skipping unregistration (user can still logout)"
      );
      return { success: true }; // Don't block logout
    }

    const { getAuthenticationToken } = AuthService;

    let deviceNotificationResponse = "";
    // Determine the operating system and platform for unregistration
    const osType = getOperatingSystem({ circadiaEngineProvider });
    const isWebBrowser =
      !window.electron && !circadiaEngineProvider.isWindowCircadia();

    try {
      if (
        window.electron ||
        circadiaEngineProvider.isWindowCircadia() ||
        isWebBrowser
      ) {
        deviceNotificationResponse = await DELETE(
          apiRoute,
          {
            notification_token:
              osType === "android"
                ? String(notificationToken).trim()
                : String(notificationToken).trim().replace(/\0/g, ""),
            os_type: isWebBrowser ? "web" : osType,
            platform: isWebBrowser ? "web" : "native",
          },
          {
            Authorization: `Bearer ${await getAuthenticationToken()}`,
          }
        );

        // If it's a web browser, also unsubscribe from FCM
        if (isWebBrowser) {
          const { webPushManager } = await import(
            "../../../../util/webPushNotifications"
          );
          await webPushManager.unsubscribeFromPushNotifications();
        }
      }
    } catch (e) {
      console.error(
        "❌ [Logout] Push notification unregistration failed (continuing logout):",
        deviceNotificationResponse,
        e
      );
      // Don't throw - allow logout to continue
    }

    return { success: true, data: deviceNotificationResponse };
  };

  return {
    registerDeviceForPushNotifications,
    unregisterDeviceForPushNotifications,
  };
};
